<template>
  <form
    id="show-adjust-timesheets-form"
    :class="{ 'adjust-mode': allowAdjustTimesheets }"
  >
    <div
      v-show="allowAdjustTimesheets"
      class="form-fields"
    >
      <div class="row form-group">
        <div class="col-md-6">
          <label class="control-label">Pay period:</label>
          <AppSelect
            v-model="data.pay_period"
            :options="payPeriods"
            disabled
          />
        </div>
      </div>
      <div class="row form-group">
        <div class="col-md-6">
          <label class="control-label">Date from:</label>
          <AppDatePicker
            v-model="data.date_from"
            type="daily"
            data-testid="date-from-picker"
            :min-date="minDateFrom"
            :max-date="maxDateTo"
            :show-quick-pick="false"
            extra-picker-classes="flex-grow"
          />
        </div>
        <div class="col-md-6">
          <label class="control-label">Date to:</label>
          <AppDatePicker
            v-model="data.date_to"
            type="daily"
            data-testid="date-to-picker"
            :min-date="minDateFrom"
            :max-date="maxDateTo"
            :show-quick-pick="false"
            extra-picker-classes="flex-grow"
          />
        </div>
      </div>
    </div>

    <div
      v-loading="loading"
      class="timesheet-ranges"
    >
      <div
        v-if="rangesResult && allowAdjustTimesheets && rangesResult.current_ranges"
        class="form-group timesheet-current-ranges"
      >
        <label class="section-title">Past Timesheets</label>
        <div class="row">
          <TimesheetRange
            v-for="(range, index) in rangesResult.current_ranges"
            :key="index"
            :width="rangePercentage(range.from, range.to)"
            range-class="old-range"
            :progress-mode="allowAdjustTimesheets"
            title=""
          >
            {{ humanDate(range.from) }} - {{ humanDate(range.to) }}
          </TimesheetRange>
        </div>
      </div>

      <div
        v-if="rangesResult"
        class="form-group new-ranges"
      >
        <label class="section-title">{{ timesheetsTitle }}</label>
        <div
          v-if="rangesResult.ranges"
          class="row"
        >
          <TimesheetRange
            v-for="range in rangesResult.ranges"
            :key="range.from"
            :title="`Past pay period (${payPeriodLabel})`"
            :tooltip="range.paid ? 'This timesheet has already been approved and paid' : ''"
            :progress-mode="allowAdjustTimesheets"
            :width="rangePercentage(range.from, range.to)"
            :range-class="range.paid ? 'paid-range' : 'new-range'"
          >
            {{ humanDate(range.from) }} - {{ humanDate(range.to) }}
          </TimesheetRange>
        </div>
        <div
          v-if="anyPaidRange"
          class="info-banner my-10"
        >
          <i class="fa fa-info-circle text-blue" />
          <span class="text-blue">
            The new pay period overlaps with some paid timesheets and will be adapted to preserve the paid ones.
          </span>
        </div>
      </div>
      <div
        v-if="rangesResult"
        class="form-buttons text-right"
      >
        <AppButton
          class="mr-5"
          data-testid="cancel-adjust-timesheets"
          @click="$emit('close')"
        >
          Cancel
        </AppButton>
        <AppButton
          type="primary"
          data-testid="submit-adjust-timesheets"
          @click="submitForm()"
        >
          Submit
        </AppButton>
      </div>
    </div>
  </form>
</template>

<script>
import { formatInternalDate } from '~/utils/time_utils';
import { TimesheetsResource } from '~/resources';
import TimesheetRange from './TimesheetRange.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'AdjustTimesheetsForm',
  components: { TimesheetRange },
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    minDateFrom: {
      type: Object, // moment
      required: true,
    },
    maxDateTo: {
      type: Object, // moment
      default: () => moment(),
    },
    initialDateTo: {
      type: Object, // moment
      default: null,
    },
    initialDateFrom: {
      type: Object, // moment
      default: null,
    },
    initialPayPeriod: {
      type: String,
      default: 'weekly',
    },
    allowAdjustTimesheets: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const dateTo = this.initialDateTo;

    return {
      data: {
        pay_period: this.initialPayPeriod,
        date_from: this.initialDateFrom || this.minDateFrom,
        date_to: dateTo && dateTo.isSame(moment().format('YYYY-MM-DD')) ? dateTo.add(1, 'days') : dateTo,
        user_id: this.userId,
      },
      rangesResult: null,
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['payPeriodOptions']),
    payPeriods() {
      return this.payPeriodOptions
        .filter((option) => option.key !== 'none')
        .map((option) => ({ id: option.key, text: option.text }));
    },
    timesheetsTitle() {
      return this.allowAdjustTimesheets ? `New Timesheets (${this.payPeriodLabel})` : 'Timesheets';
    },
    prevPaidRange() {
      return this.rangesResult.paid_ranges?.[0];
    },
    nextPaidRange() {
      return this.rangesResult.paid_ranges?.[1];
    },
    prevResizableRange() {
      return this.rangesResult.resizable_ranges?.[0];
    },
    nextResizableRange() {
      return this.rangesResult.resizable_ranges?.[1];
    },
    payPeriodLabel() {
      return this.payPeriods.find((item) => item.id === this.data.pay_period)?.text;
    },
    anyPaidRange() {
      return this.rangesResult.ranges.some((range) => range.paid);
    },
  },
  watch: {
    data: {
      handler(_val, _oldVal) {
        this.calcRanges();
      },
      deep: true,
    },
  },
  mounted() {
    this.calcRanges();
  },
  methods: {
    humanDate(date) {
      return moment(date).format('MMM DD');
    },
    rangePercentage(dateFrom, dateTo) {
      let days = moment(dateTo).diff(moment(dateFrom), 'days') + 1;

      days = days > 30 ? 30 : days;
      days = days === 7 ? 30 / 4 : days;
      days = days === 16 ? 30 / 2 : days;

      return (days * 100) / 30;
    },
    payloadData() {
      const dates = {
        date_from: formatInternalDate(this.data.date_from),
        date_to: formatInternalDate(this.data.date_to),
      };

      return { ...this.data, ...dates };
    },
    async calcRanges() {
      if (!this.data.date_to) {
        return;
      }

      this.loading = true;
      try {
        // Returns: { ranges: [], resizable_ranges: [], paid_ranges: [], current_ranges: [] }
        const res = await TimesheetsResource.periodsForRange(
          { organization_id: this.organizationId },
          this.payloadData(),
          { abortReload: true },
        );

        if (res.errors) {
          this.$appMessage(res.errors.join(', '));
        } else {
          this.rangesResult = res;
        }
      } catch (e) {
        console.error(e);
        this.$appMessage('Unexpected error occured');
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      if (!confirm('Are you sure you want to adjust timesheets?')) {
        return;
      }

      this.loading = true;
      try {
        const res = await TimesheetsResource.generateTimesheets(
          { organization_id: this.organizationId },
          this.payloadData(),
          { abortReload: true },
        );

        if (res.errors) {
          this.$appMessage(res.errors.join(', '));
        } else {
          location.reload();
        }
      } catch (e) {
        this.$appMessage('Unexpected error occurred');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
